import React, { useState, useRef } from 'react';
import { FaEnvelope, FaUser, FaPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';
import config from '../config/env';

const Contact = ({ isDarkMode }) => {
  const { t } = useTranslation();
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'


  // EmailJS Configuration from Environment Variables (with fallbacks from config)
  // For Cloudflare Pages: Set these in Settings > Environment Variables
  const EMAILJS_CONFIG = {
    SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || config.emailjs.serviceId,
    TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || config.emailjs.templateId,
    PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || config.emailjs.publicKey
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Map EmailJS field names to state properties
    const fieldMapping = {
      'user_name': 'name',
      'user_email': 'email',
      'message': 'message'
    };
    
    const stateField = fieldMapping[name] || name;
    setFormData(prev => ({
      ...prev,
      [stateField]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check if environment variables are configured
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.PUBLIC_KEY) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Send email using EmailJS
      const result = await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      if (result.status === 200 || result.text === 'OK') {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        // Reset the form
        if (formRef.current) {
          formRef.current.reset();
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      } else {
        throw new Error(`EmailJS returned status: ${result.status}`);
      }
    } catch (error) {
      setSubmitStatus('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={`min-h-screen pt-4 pb-4 lg:py-16 px-4 sm:px-6 lg:px-8 transition-colors duration-300`} id="contact">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-6 lg:mb-16">
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 lg:mb-6 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('contact.title')}
          </h1>
        </div>

        {/* Contact Form */}
        <div className="max-w-5xl mx-auto">
          <div className={`relative rounded-2xl p-5 sm:p-6 lg:p-12 transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/95 border border-purple-400/20 shadow-2xl shadow-purple-500/10' 
              : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-2xl shadow-blue-500/10'
          } backdrop-blur-sm`}>
            
            {/* Form Header */}
            <div className="text-center mb-6 lg:mb-8">
              <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full mb-3 lg:mb-4 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-purple-500/20 border border-purple-400/30' 
                  : 'bg-blue-500/20 border border-blue-400/30'
              }`}>
                <FaEnvelope className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 transition-colors duration-300 ${
                  isDarkMode ? 'text-purple-400' : 'text-blue-600'
                }`} />
              </div>
              <h2 className={`text-xl sm:text-2xl lg:text-3xl font-bold mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('contact.formTitle')}
              </h2>
              <p className={`text-sm sm:text-base transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t('contact.formSubtitle')}
              </p>
            </div>

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Name Field */}
                  <div className="space-y-2">
                    <label className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <FaUser className="w-4 h-4" />
                      {t('contact.name')}
                    </label>
                    <input
                      type="text"
                      name="user_name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gray-700/50 border border-gray-600/50 text-white placeholder-gray-400 focus:border-purple-400 focus:bg-gray-700/70' 
                          : 'bg-white/80 border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white'
                      } focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                        isDarkMode ? 'focus:ring-purple-500' : 'focus:ring-blue-500'
                      }`}
                      placeholder={t('contact.namePlaceholder')}
                    />
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <FaEnvelope className="w-4 h-4" />
                      {t('contact.email')}
                    </label>
                    <input
                      type="email"
                      name="user_email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 rounded-xl transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gray-700/50 border border-gray-600/50 text-white placeholder-gray-400 focus:border-purple-400 focus:bg-gray-700/70' 
                          : 'bg-white/80 border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white'
                      } focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                        isDarkMode ? 'focus:ring-purple-500' : 'focus:ring-blue-500'
                      }`}
                      placeholder={t('contact.emailPlaceholder')}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-2">
                  {/* Message Field */}
                  <label className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <FaPaperPlane className="w-4 h-4" />
                    {t('contact.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className={`w-full px-4 py-3 rounded-xl resize-none transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-gray-700/50 border border-gray-600/50 text-white placeholder-gray-400 focus:border-purple-400 focus:bg-gray-700/70' 
                        : 'bg-white/80 border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white'
                    } focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                      isDarkMode ? 'focus:ring-purple-500' : 'focus:ring-blue-500'
                    }`}
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                  isSubmitting
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:scale-105 hover:shadow-lg'
                } ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40' 
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('contact.sending')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <FaPaperPlane className="w-5 h-5" />
                    {t('contact.sendMessage')}
                  </div>
                )}
              </button>
            </form>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-400/30 text-green-400 text-center">
                <p className="font-semibold">{t('contact.successTitle')}</p>
                <p className="text-sm mt-1">{t('contact.successSubtitle')}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-400 text-center">
                <p className="font-semibold">{t('contact.errorTitle')}</p>
                <p className="text-sm mt-1">{t('contact.errorSubtitle')}</p>
              </div>
            )}
    </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
